import { Injectable } from "@angular/core";
import Dexie, { Table } from "dexie";

interface StorageItem {
	id?: number;
	key: string;
	value: any;
}

class StorageDatabase extends Dexie {
	storage!: Table<StorageItem>;

	constructor() {
		super("StorageDatabase");
		this.version(1).stores({
			storage: "++id, key, value"
		});
	}
}

@Injectable({
	providedIn: "root"
})
export class StorageService {
	private db: StorageDatabase;

	constructor() {
		this.db = new StorageDatabase();
	}

	async clearStorage() {
		return await this.db.storage.clear();
	}

	async getKeyAsArray(keysAsArray: string[]): Promise<any> {
		const item = await this.db.storage.where("key").equals(keysAsArray[0]).first();
		return item ? item.value : null;
	}

	// Add transaction wrapping for better performance
	async get(keysAsString: string): Promise<any> {
		const keysAsArray = keysAsString.split(".");
		try {
			// Use transaction for better performance
			return await this.db.transaction("r", this.db.storage, async () => {
				const value = await this.getKeyAsArray(keysAsArray);
				if (value === null) return undefined;
				if (keysAsArray.length === 1) return value;
				return keysAsArray.slice(1).reduce((object, key) => object && object[key], value);
			});
		} catch (error) {
			console.error("Error getting value from storage:", error);
			return undefined;
		}
	}

	async removeOrgFromAllQuerys(name: string): Promise<any> {
		const keys = await this.db.storage.toCollection().primaryKeys();
		const matchingItems = await this.db.storage.where("key").startsWith(name).toArray();

		for (const item of matchingItems) {
			const settings = item.value;
			Object.entries(settings).forEach((setting: any) => {
				const settingString = item.key + "." + setting[0] + ".query";
				const query = setting[1].query;
				if (query.organization) query.organization = "";

				this.set(settingString, query);
			});
		}
	}

	async remove(keysAsString: string) {
		const keysAsArray = keysAsString.split(".");
		if (keysAsArray.length === 1) {
			await this.db.storage.where("key").equals(keysAsArray[0]).delete();
		} else {
			const object = await this.get(keysAsArray.slice(0, -1).join("."));
			if (object !== undefined) {
				delete object[keysAsArray[keysAsArray.length - 1]];
				await this.set(keysAsArray.slice(0, -1).join("."), object);
			}
		}
	}

	async set(keysAsString: string, value: any) {
		const keysAsArray = keysAsString.split(".");

		// Use transaction for atomic operations
		return await this.db.transaction("rw", this.db.storage, async () => {
			if (keysAsArray.length === 1) {
				const existingItem = await this.db.storage.where("key").equals(keysAsArray[0]).first();
				if (existingItem) {
					await this.db.storage.update(existingItem.id!, { value });
				} else {
					await this.db.storage.add({ key: keysAsArray[0], value });
				}
			} else {
				const oldObject = (await this.get(keysAsArray[0])) || {};
				const newObject = {};
				keysAsArray.slice(1).reduce((accumulator, currentValue, index, array) => {
					if (index === array.length - 1) return (accumulator[currentValue] = value);
					return (accumulator[currentValue] = {});
				}, newObject);
				await this.set(keysAsArray[0], { ...oldObject, ...newObject });
			}
		});
	}

	// Additional helper method to get all keys (similar to Ionic Storage)
	async keys(): Promise<string[]> {
		const items = await this.db.storage.toArray();
		return items.map((item) => item.key);
	}
}
